import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import { TextStyle } from "@tiptap/extension-text-style";
import { useEffect } from "react";
import { splitListItem } from "prosemirror-schema-list";

/* ===== EXTENSIONS ===== */
const buildExtensions = () => [
  StarterKit,
  ListItem,
  BulletList,
  OrderedList,
  TextStyle,
  Underline,
  Highlight,
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
];

export default function TypedEditor({ value, onChange, settings, boxName = "default" }) {
  // Unique CSS class per box — prevents styles bleeding across editors
  const scopeClass = `typed-editor-${boxName}`;

  const editor = useEditor({
    extensions: buildExtensions(),
    content: "",

    editorProps: {
      attributes: {
        class: `focus:outline-none min-h-[100px] p-2 ${scopeClass}`,
      },

      handleKeyDown(view, event) {
        if (event.key === "Enter") {
          const { state, dispatch } = view;
          const listItem = state.schema.nodes.listItem;

          if (state.selection.$from.parent.type === listItem) {
            event.preventDefault();
            return splitListItem(listItem)(state, dispatch);
          }
        }
        return false;
      },
    },

    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  /* ===== HYDRATE CONTENT ===== */
  useEffect(() => {
    if (!editor) return;
    if (value == null) return;

    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const s = settings?.style || {};
  const t = settings?.tools || {};

  return (
    <div className="border rounded-lg bg-white">

      {/* ===== TOOLBAR (manual optional controls) ===== */}
      <div className="flex flex-wrap gap-2 border-b px-2 py-1 bg-gray-50">

        {t.bold && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className="btn text-xs"
          >
            B
          </button>
        )}

        {t.italic && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className="btn text-xs"
          >
            I
          </button>
        )}

        {t.underline && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className="btn text-xs"
          >
            U
          </button>
        )}

        {t.highlight && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className="btn text-xs"
          >
            H
          </button>
        )}

        {t.align && (
          <>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              className="btn text-xs"
            >
              L
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("center").run()}
              className="btn text-xs"
            >
              C
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              className="btn text-xs"
            >
              R
            </button>
          </>
        )}

        {t.bulletList && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className="btn text-xs"
          >
            •
          </button>
        )}

        {t.orderedList && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className="btn text-xs"
          >
            1.
          </button>
        )}
      </div>

      {/* ===== SCOPED PER-BOX STYLING — prevents cross-editor bleed ===== */}
      <div>
        <style>
          {`
            /* scopeClass is set via editorProps.attributes.class, which Tiptap places
               directly on the .ProseMirror root element — so use same-element selector */
            .${scopeClass}.ProseMirror {
              font-family: "${s.fontFamily || "Times New Roman"}" !important;
              font-size: ${s.fontSize || 16}px !important;
              line-height: ${s.lineHeight || 1.5} !important;
              text-align: ${s.textAlign || "left"} !important;
              font-weight: ${t.bold ? "700" : "400"} !important;
              font-style: ${t.italic ? "italic" : "normal"} !important;
              text-decoration: ${t.underline ? "underline" : "none"} !important;
              background-color: ${t.highlight ? "#fef08a" : "transparent"} !important;
            }

            .${scopeClass}.ProseMirror p {
              margin-bottom: ${s.paragraphSpacing ?? 8}px;
            }

            .${scopeClass}.ProseMirror ul {
              list-style-type: ${
                s.bulletType === "circle" ? "circle" :
                s.bulletType === "square" ? "square" : "disc"
              };
              padding-left: 18px;
            }

            .${scopeClass}.ProseMirror ol {
              list-style-type: ${
                s.numberType === "roman" ? "upper-roman" :
                s.numberType === "alpha" ? "upper-alpha" : "decimal"
              };
              padding-left: 18px;
            }
          `}
        </style>

        <EditorContent editor={editor} />
      </div>
    </div>
  );
}